import { Modal, Form, Input, InputNumber, TreeSelect, Switch, DatePicker, App } from 'antd'
import { useEffect, useState } from 'react'
import dayjs, { type Dayjs } from 'dayjs'
import { updateTenant, type TenantLicense } from '../services/tenant-api'
import { type Tenant } from '../types/tenant.types'
import { useFeatureFlagSelect } from '../hooks/useFeatureFlagSelect'

interface EditTenantModalProps {
  open: boolean
  tenant: Tenant | null
  onClose: () => void
  onSuccess: () => void
}

interface EditTenantFormValues {
  workspaceCode: string
  tenantName: string
  countryCodes: string[]
  timeZoneId: string
  isDemo: boolean
  isActive: boolean
  inactiveDate: Dayjs | null
  destroyDate: Dayjs | null
  featureFlags: string[]
  // License counts
  empCount?: number
  empPsgCount?: number
  empLveCount?: number
  empClaCount?: number
  empAttCount?: number
  empPfmCount?: number
}

export function EditTenantModal({ open, tenant, onClose, onSuccess }: EditTenantModalProps) {
  const { message } = App.useApp()
  const [form] = Form.useForm<EditTenantFormValues>()
  const { treeData } = useFeatureFlagSelect()
  const [loading, setLoading] = useState(false)
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    if (tenant && open) {
      const countryCodes = tenant.countryCodes ? tenant.countryCodes.split(',').map(c => c.trim().toLowerCase()) : []

      const enabledFlags = tenant.featureFlags.filter(ff => ff.disabledDate === null).map(ff => ff.featureFlag)

      const isCurrentlyActive = !tenant.inactiveDate
      setIsActive(isCurrentlyActive)

      const license: TenantLicense = tenant.licenseJsonb ? JSON.parse(tenant.licenseJsonb) : {}

      form.setFieldsValue({
        workspaceCode: tenant.workspaceCode,
        tenantName: tenant.tenantName,
        countryCodes,
        timeZoneId: tenant.timeZoneId,
        isDemo: tenant.isDemo,
        isActive: isCurrentlyActive,
        inactiveDate: tenant.inactiveDate ? dayjs(tenant.inactiveDate) : null,
        destroyDate: tenant.destroyDate ? dayjs(tenant.destroyDate) : null,
        featureFlags: enabledFlags,
        empCount: license.EmpCount,
        empPsgCount: license.EmpPsgCount,
        empLveCount: license.EmpLveCount,
        empClaCount: license.EmpClaCount,
        empAttCount: license.EmpAttCount,
        empPfmCount: license.EmpPfmCount
      })
    }
  }, [tenant, open, form])

  const handleSubmit = async (values: EditTenantFormValues) => {
    if (!tenant) return

    try {
      setLoading(true)

      const license: TenantLicense = {
        EmpCount: values.empCount,
        EmpPsgCount: values.empPsgCount,
        EmpLveCount: values.empLveCount,
        EmpClaCount: values.empClaCount,
        EmpAttCount: values.empAttCount,
        EmpPfmCount: values.empPfmCount
      }
      const hasLicense = Object.values(license).some(v => v !== undefined && v !== null)

      await updateTenant(tenant.tenantCode, {
        workspaceCode: values.workspaceCode,
        tenantName: values.tenantName,
        countryCodes: values.countryCodes.join(','),
        timeZoneId: values.timeZoneId,
        isDemo: values.isDemo,
        inactiveDate: values.isActive
          ? null
          : values.inactiveDate
            ? values.inactiveDate.toISOString()
            : new Date().toISOString(),
        destroyDate: values.destroyDate ? values.destroyDate.toISOString() : null,
        featureFlags: values.featureFlags,
        licenseJsonb: hasLicense ? JSON.stringify(license) : undefined
      })
      message.success('Workspace updated successfully')
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Error updating workspace:', error)
      message.error('Failed to update workspace. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  const handleActiveChange = (checked: boolean) => {
    setIsActive(checked)
    if (checked) {
      form.setFieldValue('inactiveDate', null)
    }
  }

  return (
    <Modal
      title={`Edit Workspace: ${tenant?.tenantName || ''}`}
      open={open}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      okText="Update"
      confirmLoading={loading}
      width={900}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item label="Tenant code" extra="Cannot be changed">
            <Input value={tenant?.tenantCode} disabled />
          </Form.Item>

          <Form.Item
            label="Workspace code"
            name="workspaceCode"
            normalize={value => value?.toUpperCase()}
            rules={[
              { required: true, message: 'Please enter workspace code' },
              {
                pattern: /^[A-Z0-9_-]+$/,
                message: 'Only uppercase letters, numbers, hyphens and underscores allowed'
              },
              { min: 2, message: 'Code must be at least 2 characters' },
              { max: 20, message: 'Code must not exceed 20 characters' }
            ]}
            extra="Short display code (uppercase)"
          >
            <Input placeholder="e.g., ZEALYS" />
          </Form.Item>
        </div>

        <Form.Item
          label="Workspace name"
          name="tenantName"
          rules={[
            { required: true, message: 'Please enter workspace name' },
            { min: 3, message: 'Name must be at least 3 characters' },
            { max: 100, message: 'Name must not exceed 100 characters' }
          ]}
        >
          <Input placeholder="e.g., Zealys Pte Ltd" />
        </Form.Item>

        {/* Countries field - hidden, defaults to Singapore */}
        {/* <Form.Item
          label="Countries"
          name="countryCodes"
          rules={[{ required: true, message: 'Please select at least one country' }]}
        >
          <Select
            mode="multiple"
            showSearch
            placeholder="Select countries"
            options={countryOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item> */}
        <Form.Item name="countryCodes" hidden>
          <Input type="hidden" />
        </Form.Item>

        {/* Time Zone field - hidden, defaults to Asia/Singapore */}
        {/* <Form.Item
          label="Time Zone"
          name="timeZoneId"
          rules={[{ required: true, message: 'Please select a time zone' }]}
        >
          <Select
            showSearch
            placeholder="Select a time zone"
            options={timezoneOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item> */}
        <Form.Item name="timeZoneId" hidden>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.featureFlags !== currentValues.featureFlags}>
          {({ getFieldValue }) => {
            const featureFlags: string[] = getFieldValue('featureFlags') || []
            const hasFlag = (flag: string) => featureFlags.includes(flag)

            return (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'start' }}>
                <Form.Item label="Feature flags" name="featureFlags" style={{ marginBottom: 0 }}>
                  <TreeSelect
                    treeCheckable
                    treeData={treeData}
                    placeholder="Select feature flags"
                    treeDefaultExpandAll
                    allowClear
                    showCheckedStrategy={TreeSelect.SHOW_ALL}
                  />
                </Form.Item>
                <Form.Item label="License" style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '80px 1fr 130px 1fr',
                      gap: '4px 8px',
                      alignItems: 'center'
                    }}
                  >
                    <span>People</span>
                    <Form.Item name="empCount" noStyle>
                      <InputNumber min={0} placeholder="0" size="small" style={{ width: '100%' }} />
                    </Form.Item>
                    <span>Payroll</span>
                    <Form.Item name="empPsgCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('payroll')}
                      />
                    </Form.Item>
                    <span>Leave</span>
                    <Form.Item name="empLveCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('leave')}
                      />
                    </Form.Item>
                    <span>Claim</span>
                    <Form.Item name="empClaCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('claim')}
                      />
                    </Form.Item>
                    <span>Attendance</span>
                    <Form.Item name="empAttCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('attendance')}
                      />
                    </Form.Item>
                    <span>Performance review</span>
                    <Form.Item name="empPfmCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('performance')}
                      />
                    </Form.Item>
                  </div>
                </Form.Item>
              </div>
            )
          }}
        </Form.Item>

        {/* <Form.Item
          label="Demo workspace"
          name="isDemo"
          valuePropName="checked"
          extra="Mark this workspace as a demo environment"
        >
          <Switch />
        </Form.Item> */}

        <Form.Item
          label="Status"
          extra={isActive ? 'Workspace is currently active' : 'Workspace will be marked as inactive'}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Form.Item name="isActive" valuePropName="checked" noStyle>
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" onChange={handleActiveChange} />
            </Form.Item>
            {!isActive && (
              <Form.Item
                name="inactiveDate"
                noStyle
                rules={[{ required: !isActive, message: 'Please select inactive date' }]}
              >
                <DatePicker style={{ width: 200 }} format="YYYY-MM-DD" placeholder="Select inactive date" />
              </Form.Item>
            )}
          </div>
        </Form.Item>

        <Form.Item label="Destroy date" name="destroyDate" extra="Scheduled date for workspace destruction">
          <DatePicker style={{ width: 200 }} format="YYYY-MM-DD" placeholder="Select destroy date" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
